import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Check, X, Ban, RotateCcw, Mail, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const TABS = [
  { id: "pending", label: "In attesa" },
  { id: "approved", label: "Approvati" },
  { id: "suspended", label: "Sospesi" },
  { id: "rejected", label: "Rifiutati" },
  { id: "all", label: "Tutti" },
];

const STATUS_STYLE = {
  pending: "bg-blue-50 text-blue-700",
  approved: "bg-emerald-50 text-emerald-700",
  suspended: "bg-amber-50 text-amber-700",
  rejected: "bg-slate-100 text-slate-600",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function AdminUsers() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ users: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?status=${tab}`);
      setData(data);
    } catch (e) {
      toast.error("Impossibile caricare gli utenti");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const act = async (uid, action) => {
    setBusy(uid + action);
    try {
      await api.post(`/admin/users/${uid}/${action}`);
      toast.success("Fatto");
      await load();
    } catch (e) {
      toast.error("Azione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 pt-6" data-testid="admin-users">
      <h1 className="font-display font-bold text-2xl text-slate-900">Utenti</h1>
      <p className="text-sm text-slate-500 mt-0.5">Gestione accessi Closed Beta</p>

      <div className="mt-4 flex gap-2 overflow-x-auto hide-scrollbar -mx-4 px-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`shrink-0 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {t.label}
            {data.counts?.[t.id] != null && <span className="ml-1.5 opacity-70">{data.counts[t.id]}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : data.users.length === 0 ? (
        <p className="py-16 text-center text-slate-400 text-sm">Nessun utente in questa categoria.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {data.users.map((u) => {
            const st = u.approval_status;
            return (
              <div key={u.id} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid={`admin-user-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{u.name || "—"}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      {u.email_verified ? <MailCheck className="w-3 h-3 text-emerald-500" /> : <Mail className="w-3 h-3 text-slate-300" />}
                      {u.email}
                    </p>
                  </div>
                  <span className={`shrink-0 text-[11px] font-bold px-2 py-1 rounded-full capitalize ${STATUS_STYLE[st] || "bg-slate-100 text-slate-600"}`}>{st}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  <span>Registrato: <b className="text-slate-700 font-semibold">{fmt(u.created_at)}</b></span>
                  <span>Approvato: <b className="text-slate-700 font-semibold">{fmt(u.approved_at)}</b></span>
                  <span>Trial fine: <b className="text-slate-700 font-semibold">{fmt(u.trial_ends_at)}</b></span>
                  <span>Piano: <b className="text-slate-700 font-semibold capitalize">{u.subscription_plan || "—"}</b></span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {st === "pending" && (
                    <>
                      <ActBtn onClick={() => act(u.id, "approve")} busy={busy === u.id + "approve"} icon={Check} label="Approva" tone="bg-emerald-600" tid={`approve-${u.id}`} />
                      <ActBtn onClick={() => act(u.id, "reject")} busy={busy === u.id + "reject"} icon={X} label="Rifiuta" tone="bg-slate-500" tid={`reject-${u.id}`} />
                    </>
                  )}
                  {st === "approved" && (
                    <ActBtn onClick={() => act(u.id, "suspend")} busy={busy === u.id + "suspend"} icon={Ban} label="Sospendi" tone="bg-amber-600" tid={`suspend-${u.id}`} />
                  )}
                  {(st === "suspended" || st === "rejected") && (
                    <ActBtn onClick={() => act(u.id, "reactivate")} busy={busy === u.id + "reactivate"} icon={RotateCcw} label="Riattiva" tone="bg-blue-600" tid={`reactivate-${u.id}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ActBtn = ({ onClick, busy, icon: Icon, label, tone, tid }) => (
  <button onClick={onClick} disabled={busy} data-testid={`admin-action-${tid}`}
    className={`inline-flex items-center gap-1.5 rounded-full ${tone} text-white text-xs font-semibold px-3.5 py-2 disabled:opacity-60`}>
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {label}
  </button>
);
